import { useEffect, useRef, useState } from 'react';
import { useLang } from '../../../i18n/LangContext';

const STATS = [
  { key: 'mission_stat1_num', subKey: 'mission_stat1_label', icon: 'ri-calendar-check-line' },
  { key: 'mission_stat2_num', subKey: 'mission_stat2_label', icon: 'ri-group-line' },
  { key: 'mission_stat3_num', subKey: 'mission_stat3_label', icon: 'ri-graduation-cap-line' },
  { key: 'mission_stat4_num', subKey: 'mission_stat4_label', icon: 'ri-trophy-line' },
];

const PILLARS = [
  { icon: 'ri-book-open-line',      titleKey: 'mission_pillar1_title', descKey: 'mission_pillar1_desc' },
  { icon: 'ri-line-chart-line',     titleKey: 'mission_pillar2_title', descKey: 'mission_pillar2_desc' },
  { icon: 'ri-building-2-line',     titleKey: 'mission_pillar3_title', descKey: 'mission_pillar3_desc' },
  { icon: 'ri-heart-line',          titleKey: 'mission_pillar4_title', descKey: 'mission_pillar4_desc' },
];

export default function MissionBulgaria() {
  const { t } = useLang();
  const sectionRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="mission" ref={sectionRef} className="relative py-28 px-6 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://public.readdy.ai/ai/img_res/275f551035af5e231d4195f4e86e267a.jpg"
          alt="Bulgaria"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a0e1a]/97 via-[#0a0e1a]/85 to-[#0a0e1a]/60" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0e1a] via-transparent to-[#0a0e1a]/40" />
      </div>

      {/* Decorative gold lines */}
      <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-transparent via-[#d4af37]/60 to-transparent z-10" />

      <div className="relative z-10 max-w-6xl mx-auto">

        {/* Header */}
        <div className={`text-center mb-16 transition-all duration-1000 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full border border-[#d4af37]/40 bg-[#d4af37]/10 mb-6">
            <span className="text-xl">🇧🇬</span>
            <span className="text-[#d4af37] text-sm font-semibold tracking-widest uppercase">{t('mission_badge')}</span>
          </div>

          <h2 className="text-5xl md:text-6xl font-bold mb-5 leading-tight">
            <span className="bg-gradient-to-r from-[#d4af37] via-[#f4d03f] to-[#d4af37] bg-clip-text text-transparent">
              {t('mission_title')}
            </span>
          </h2>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto leading-relaxed">
            {t('mission_subtitle')}
          </p>
          <div className="w-24 h-1 bg-gradient-to-r from-[#d4af37] to-[#f4d03f] mx-auto mt-6" />
        </div>

        {/* Main content grid */}
        <div className="grid lg:grid-cols-2 gap-14 items-start mb-16">

          {/* Left — story */}
          <div className={`space-y-6 transition-all duration-1000 delay-200 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
            {/* Personal note */}
            <div className="relative pl-6 border-l-2 border-[#d4af37]/60">
              <p className="text-lg text-gray-200 leading-relaxed italic">
                "{t('mission_quote')}"
              </p>
              <div className="mt-3 text-[#d4af37] font-semibold text-sm">— {t('mission_quote_author')}</div>
            </div>

            <p className="text-gray-300 leading-relaxed text-base"
              dangerouslySetInnerHTML={{
                __html: t('mission_p1')
                  .replace(/<b>/g, '<strong class="text-[#d4af37]">')
                  .replace(/<\/b>/g, '</strong>')
              }}
            />
            <p className="text-gray-300 leading-relaxed text-base"
              dangerouslySetInnerHTML={{
                __html: t('mission_p2')
                  .replace(/<b>/g, '<strong class="text-[#d4af37]">')
                  .replace(/<\/b>/g, '</strong>')
              }}
            />
            <p className="text-gray-300 leading-relaxed text-base"
              dangerouslySetInnerHTML={{
                __html: t('mission_p3')
                  .replace(/<b>/g, '<strong class="text-[#d4af37]">')
                  .replace(/<\/b>/g, '</strong>')
              }}
            />

            {/* Since badge */}
            <div className="inline-flex items-center gap-3 px-5 py-3 rounded-xl bg-gradient-to-r from-[#d4af37]/15 to-[#d4af37]/5 border border-[#d4af37]/30">
              <div className="w-8 h-8 flex items-center justify-center">
                <i className="ri-flag-line text-[#d4af37] text-xl" />
              </div>
              <div>
                <div className="text-[#d4af37] font-bold text-sm">{t('mission_since_label')}</div>
                <div className="text-white font-semibold">{t('mission_since_val')}</div>
              </div>
            </div>
          </div>

          {/* Right — photo + pillars */}
          <div className={`space-y-5 transition-all duration-1000 delay-400 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
            {/* Photo */}
            <div className="relative rounded-2xl overflow-hidden border border-[#d4af37]/25 shadow-2xl shadow-black/60">
              <img
                src="https://static.readdy.ai/image/5490f3a4ad0e9eb55a0769efce385370/0288d858fc7c0d32359102849464040d.jpeg"
                alt="Alex Zinger in Bulgaria"
                className="w-full h-72 object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a0e1a]/80 via-transparent to-transparent" />
              <div className="absolute bottom-4 left-4 right-4">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">🇧🇬</span>
                  <div>
                    <div className="text-white font-semibold text-sm">{t('mission_photo_caption')}</div>
                    <div className="text-[#d4af37] text-xs">{t('mission_photo_sub')}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Stats row */}
            <div className="grid grid-cols-2 gap-3">
              {STATS.map((s, i) => (
                <div
                  key={i}
                  className={`bg-gradient-to-br from-[#1a1f35] to-[#0d1120] border border-[#d4af37]/20 rounded-xl p-4 flex items-center gap-3 transition-all duration-700`}
                  style={{ transitionDelay: `${500 + i * 100}ms` }}
                >
                  <div className="w-10 h-10 flex items-center justify-center bg-[#d4af37]/10 rounded-lg shrink-0">
                    <i className={`${s.icon} text-[#d4af37] text-lg`} />
                  </div>
                  <div>
                    <div className="text-[#d4af37] font-bold text-lg leading-none">{t(s.key)}</div>
                    <div className="text-gray-400 text-xs mt-0.5">{t(s.subKey)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Pillars */}
        <div className={`transition-all duration-1000 delay-600 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <h3 className="text-center text-2xl font-bold text-[#d4af37] mb-8">{t('mission_pillars_title')}</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {PILLARS.map((p, i) => (
              <div
                key={i}
                className="group bg-gradient-to-br from-[#1a1f35]/90 to-[#0a0e1a]/90 border border-[#d4af37]/20 hover:border-[#d4af37]/60 rounded-2xl p-6 text-center transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-[#d4af37]/10 cursor-default"
              >
                <div className="w-14 h-14 flex items-center justify-center bg-[#d4af37]/10 group-hover:bg-[#d4af37]/20 rounded-xl mx-auto mb-4 transition-all duration-300">
                  <i className={`${p.icon} text-[#d4af37] text-2xl`} />
                </div>
                <h4 className="text-white font-semibold mb-2 text-sm">{t(p.titleKey)}</h4>
                <p className="text-gray-400 text-xs leading-relaxed">{t(p.descKey)}</p>
              </div>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
}
